if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        // Narrow the gallery to the selected variant's image plus shared lifestyle media before the
        // slider/lightbox initialise, so they build on the already-filtered DOM (no first-paint flash).
        this.setupMediaFiltering();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setupMediaFiltering() {
        this.filterMedia = this.dataset.filterMedia === 'true';
        if (!this.filterMedia) return;

        // updateMedia() re-runs init() on every variant change, so cache the pristine masters once.
        // Re-caching from an already-filtered DOM would shrink the master set and drop hidden media.
        if (this._filteringReady) return;

        this.lifestyleIds = (this.dataset.lifestyleMedia || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        if (!this.lifestyleIds.length) {
          this.filterMedia = false; // nothing to lead with, leave the full gallery untouched
          return;
        }

        // Pristine copies of every slide and thumb, in original order, to rebuild any subset from.
        const thumbList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (!this.elements || !this.elements.mediaList) {
          this.filterMedia = false;
          return;
        }
        this.masterSlides = [...this.elements.mediaList.querySelectorAll('.product__media-item')].map((node) => ({
          id: String(node.dataset.mediaId),
          node: node.cloneNode(true),
        }));
        this.masterThumbs = thumbList
          ? [...thumbList.querySelectorAll('.product__thumbs-item')].map((node) => ({
              id: String(node.dataset.target),
              node: node.cloneNode(true),
            }))
          : [];
        this._filteringReady = true;

        // On load show only the lifestyle media; a selected variant's image is revealed on demand.
        this.applyMediaFilter(this.lifestyleIds, { initial: true });
      }

      variantMediaIds(variant) {
        const own = variant && variant.featured_media && String(variant.featured_media.id);
        if (!own || this.lifestyleIds.includes(own)) return this.lifestyleIds;
        return [...this.lifestyleIds, own];
      }

      applyMediaFilter(allowedIds, { initial = false } = {}) {
        if (!this.filterMedia || !this.masterSlides) return false;

        const signature = allowedIds.join(',');
        if (signature === this._filterSignature) return false; // set unchanged, nothing to rebuild
        this._filterSignature = signature;

        const allowed = new Set(allowedIds);
        const rebuild = (listEl, masters) => {
          if (!listEl) return;
          const nodes = masters.filter((master) => allowed.has(master.id)).map((master) => master.node.cloneNode(true));
          listEl.replaceChildren(...nodes);
        };

        if (initial) {
          rebuild(this.elements.mediaList, this.masterSlides);
          rebuild(this.querySelector('[id^="Slider-Thumbnails"]'), this.masterThumbs);
          return false; // init() will build the slider + lightbox on this filtered DOM
        }

        // Swiper's loop-mode dynamic-slide API is unreliable, so we rebuild + re-init. Hold the
        // gallery hidden across the sync rebuild (a CSS class), then drop it to fade back in.
        this.classList.add('product__media-gallery--refiltering');
        void this.offsetWidth; // commit opacity:0 before the reflow so the flash never paints

        this.destroySliderInstances();
        rebuild(this.elements.mediaList, this.masterSlides);
        rebuild(this.querySelector('[id^="Slider-Thumbnails"]'), this.masterThumbs);
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.updateMediaLayout();
        if (this.enableImageZoom) this.buildLightbox();

        requestAnimationFrame(() => this.classList.remove('product__media-gallery--refiltering'));
        return true;
      }

      destroySliderInstances() {
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }
      }

      filteredRealIndexOf(mediaId) {
        if (!this.elements || !this.elements.mediaList) return -1;
        const realSlides = [
          ...this.elements.mediaList.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)'),
        ];
        return realSlides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        // Bind the zoom trigger once (init() re-runs on every variant change); it resolves the
        // lightbox index from the live (filtered) DOM so it stays correct after a re-filter.
        if (!this._zoomBound) {
          this._zoomBound = true;
          FoxTheme.utils.addEventDelegate({
            selector: '.js-photoswipe--zoom',
            context: this,
            handler: (event, media) => {
              if (media.dataset.mediaType !== 'image' || !this.lightbox) return;
              const index = this.filterMedia
                ? this.filteredRealIndexOf(media.dataset.mediaId)
                : Number(media.dataset.mediaIndex);
              this.lightbox.loadAndOpen(index < 0 ? 0 : index);
            },
          });
        }
        this.buildLightbox();
      }

      buildLightbox() {
        this.destroyLightbox();
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();
      }

      destroyLightbox() {
        if (this.lightbox) {
          try {
            this.lightbox.destroy();
          } catch (error) {
            /* lightbox already torn down */
          }
          this.lightbox = null;
        }
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        // Reveal this variant's image alongside the lifestyle media, then target it. The filtered
        // slide order differs from featured_media.position, so resolve the index live.
        if (this.filterMedia) {
          const rebuilt = this.applyMediaFilter(this.variantMediaIds(variant));
          if (this.sliderInstance && this.sliderInstance.slider) {
            const filteredIndex = this.filteredRealIndexOf(variant.featured_media.id);
            const targetIndex = filteredIndex < 0 ? 0 : filteredIndex;
            // A rebuild re-inits the slider at slide 0 behind the hidden gallery; jump with speed 0
            // so the reveal lands on the variant's image instead of sweeping through the lifestyle set.
            this.sliderInstance.slider.slideToLoop(targetIndex, rebuilt ? 0 : undefined);
          } else {
            this.sortMediaItems(variant);
          }
          return;
        }

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
